import { type FC, useState } from "react";

import { ArrowRight, Check, ChevronDown, CircleHelp, Lock, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { Sheet, SheetContent, SheetTitle } from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import SSNInput from "@/shared/components/SSNInput";
import { useSSNField } from "@/shared/hooks/useSSNField";
import { useSaveRegularSSN } from "@/shared/hooks/useSaveRegularSSN";
import { isValidSSN, cleanSSN } from "@/shared/utils/formatters";
import { cn } from "@/shared/utils/core";
import { supabase } from "@/shared/lib/supabase";
import { SETTLE_TASK_IDS } from "@/shared/constants/taskIds";
import { notifyEinSsnSubmitted } from "@/shared/utils/slackNotifications";

const FAQ_ITEMS = [
  {
    question: "Does this put {firstName}'s debts in my name?",
    answer:
      "No. You're listed as the person in charge of the estate, and its debts are paid from the estate's money.",
  },
  {
    question: "Will it affect my credit?",
    answer: "No. Applying for a tax ID isn't a credit check.",
  },
  {
    question: "Will it affect my own taxes?",
    answer:
      "No. Applying for the estate's tax ID doesn't start an audit, and the estate's taxes are paid from the estate, not by you.",
  },
  {
    question: "Who sees my number?",
    answer:
      "The IRS sees it on this application. Elayne stores it encrypted and doesn't use it for anything else.",
  },
];

const TOTAL_SCREENS = 2;

interface GetEINDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  estateId: number;
  userId: number;
  userEmail: string;
  firstName: string;
}

export const GetEINDrawer: FC<GetEINDrawerProps> = ({
  isOpen,
  onClose,
  estateId,
  userId,
  userEmail,
  firstName,
}) => {
  const [screen, setScreen] = useState<1 | 2>(1);
  const [faqOpen, setFaqOpen] = useState(false);
  const { ssnValue, setSsnValue, ssnVisible, setSsnVisible, ssnInputKey } = useSSNField(null);
  const { saveSSN, isLoading: isSaving } = useSaveRegularSSN();
  const queryClient = useQueryClient();

  const rawSSN = cleanSSN(ssnValue);
  const isValid = isValidSSN(rawSSN);

  const handleSubmitSSN = async () => {
    if (!isValid) return;

    try {
      await saveSSN({ ssn: rawSSN, userId });

      const now = new Date().toISOString();
      const { error } = await supabase.from("estate_tasks").upsert(
        {
          estate_id: estateId,
          task_id: SETTLE_TASK_IDS.GET_EIN,
          status: "in_progress",
          started_at: now,
          last_active_at: now,
          answers: { ssn_submitted_at: now },
        },
        { onConflict: "estate_id,task_id" },
      );
      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
      notifyEinSsnSubmitted(estateId).catch(() => {});
      setScreen(2);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to submit";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const handleClose = () => {
    setScreen(1);
    setFaqOpen(false);
    setSsnValue("");
    setSsnVisible(false);
    onClose();
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <SheetContent
        side="right"
        showClose={false}
        overlayClassName="bg-black/30 backdrop-blur-[2px]"
        className="w-full sm:max-w-none sm:w-[50vw] p-0 flex flex-col border-l border-primary shadow-[0_0_24px_0_rgba(0,0,0,0.09)] bg-white"
      >
        <div className="flex flex-col gap-3 border-b border-primary p-6 shrink-0">
          <div className="flex items-center gap-3 w-full">
            <span className="text-base font-medium tracking-[-0.02em] text-secondary shrink-0">
              Step 1
            </span>
            <span className="h-1 w-1 rounded-full bg-stone-400 shrink-0" aria-hidden="true" />
            <span className="text-base font-medium tracking-[-0.02em] text-secondary flex-1 min-w-0 truncate">
              Get the estate's tax ID
            </span>
            <button
              onClick={handleClose}
              className="flex h-5 w-5 shrink-0 items-center justify-center hover:opacity-70"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center gap-4 w-full">
            <div className="relative flex-1 h-2 rounded-full bg-background overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-brand transition-all duration-300"
                style={{ width: `${(screen / TOTAL_SCREENS) * 100}%` }}
              />
            </div>
            <span className="text-sm text-secondary shrink-0 whitespace-nowrap">
              {screen} of {TOTAL_SCREENS}
            </span>
          </div>
        </div>

        <SheetTitle className="sr-only">Get the estate's tax ID</SheetTitle>

        {screen === 1 ? (
          <div className="flex-1 min-h-0 overflow-y-auto bg-white p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <h4>Add your Social Security number</h4>
                <p className="text-sm text-secondary">
                  The IRS asks for the Social Security number of the person in charge of the estate.
                  It's used only to apply for the estate's tax ID.
                </p>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium leading-[14px]">
                    Social Security number
                  </label>
                  <SSNInput
                    key={ssnInputKey}
                    valueFormatted={ssnValue}
                    onChangeFormatted={setSsnValue}
                    ssnVisible={ssnVisible}
                    toggleVisible={() => setSsnVisible((v) => !v)}
                    isDetailMissing={false}
                  />
                </div>
                <div className="flex items-center gap-2 px-1">
                  <Lock className="h-3 w-3 shrink-0 text-tertiary" />
                  <p className="text-xs text-tertiary">
                    Encrypted and never shared outside this application
                  </p>
                </div>
              </div>

              <Collapsible open={faqOpen} onOpenChange={setFaqOpen}>
                <CollapsibleTrigger className="flex items-center gap-2">
                  <CircleHelp className="h-4 w-4 text-secondary" />
                  <span className="text-sm text-secondary">Why we ask for this</span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 text-secondary transition-transform duration-200",
                      faqOpen && "rotate-180",
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-3 flex flex-col gap-3 pl-6">
                    {FAQ_ITEMS.map((item) => (
                      <div key={item.question} className="flex flex-col gap-1">
                        <p className="text-xs font-medium">
                          {item.question.replace("{firstName}", firstName)}
                        </p>
                        <p className="text-xs text-secondary">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto bg-subtle p-8">
            <div className="flex flex-col gap-6">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand">
                <Check className="h-4 w-4 text-white" />
              </div>
              <div className="flex flex-col gap-1">
                <h4>Next, sign the IRS form</h4>
                <p className="text-sm text-secondary">
                  We're preparing the estate's tax ID application. It arrives at {userEmail} within
                  2 business days, from Elayne, and it takes 1 signature.
                </p>
                <p className="mt-2 text-sm text-secondary">
                  Don't see the email? Check your spam folder or{" "}
                  <Button
                    variant="link"
                    size={null}
                    onClick={() => window.Intercom?.("show")}
                    className="text-sm underline"
                  >
                    get support
                  </Button>
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end border-t border-primary p-6 shrink-0">
          {screen === 1 ? (
            <Button
              variant={isValid && !isSaving ? "brand" : "subtle"}
              disabled={!isValid || isSaving}
              onClick={handleSubmitSSN}
            >
              {isSaving ? "Submitting..." : "Next"}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button variant="brand" onClick={handleClose}>
              Back to your path
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
